import React from 'react';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../api/CustomersAPI';
import Pill from '../Pill/Pill';
import { customerStatusToPillColor, customerStatusToTranslationKey } from '../../shared/customerStatusToTranslationKey';
import { Mail, Phone, Calendar, Receipt, Calendar as CalendarIcon } from 'lucide-react';
import { parseLegacyDateArray, safeParseDate } from '../../shared/timeUtils';

interface CustomerSearchResultProps {
  customer: Customer;
  onView: (customer: Customer) => void;
}

export default function CustomerSearchResult({ customer, onView }: CustomerSearchResultProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    onView(customer);
  };

  return (
    <div 
      className="group p-3 sm:p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={t('customers.search_result.view_customer_aria', { name: `${customer.name} ${customer.surname}`.trim() }) as string}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 truncate">
                {`${customer.name} ${customer.surname}`.trim() || t('customers.search_result.unnamed')}
              </h4>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs text-gray-600 mt-1">
                {customer.email && (
                  <span className="flex items-center space-x-1">
                    <Mail className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{customer.email}</span>
                  </span>
                )}
                {customer.phone && (
                  <span className="flex items-center space-x-1">
                    <Phone className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{customer.phone}</span>
                  </span>
                )}
                {customer.weddingDate && (() => {
                  let date: Date | null = null;
                  if (Array.isArray(customer.weddingDate)) {
                    date = parseLegacyDateArray(customer.weddingDate);
                  } else {
                    date = safeParseDate(customer.weddingDate);
                  }
                  return date ? (
                    <span className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3 flex-shrink-0 text-gray-400" />
                      <span className="truncate">{date.toLocaleDateString()}</span>
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Pill 
                text={t(customerStatusToTranslationKey(customer.status))}
                color={customerStatusToPillColor(customer.status)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
            <div className="flex items-center space-x-1 min-w-0">
              <Receipt className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="font-medium">{t('customers.search_result.sales')}:</span>
              <span className="truncate">{customer.sales?.length || 0}</span>
            </div>
            <div className="flex items-center space-x-1 min-w-0">
              <CalendarIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="font-medium">{t('customers.search_result.appointments')}:</span>
              <span className="truncate">{customer.meetings?.length || 0}</span>
            </div>
            <div className="flex items-center space-x-1 min-w-0">
              <CalendarIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="font-medium">{t('customers.search_result.noshow')}:</span>
              <span className="truncate">{customer.meetings?.filter(meeting => meeting.noShow)?.length || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


